import { create } from 'zustand';
import type { ActiveContext, ResolvedMembership } from './session.types';

/** localStorage key for the last-activated context tenant id (reload restore). */
export const ACTIVE_CONTEXT_STORAGE_KEY = 'entite.activeContextTenantId';

/** Persist / read / clear the last-activated context tenant id (best-effort). */
export const activeContextStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  set(tenantId: string): void {
    try {
      localStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, tenantId);
    } catch {
      /* storage unavailable — restore just won't happen */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);
    } catch {
      /* no-op */
    }
  },
};

interface ContextState {
  memberships: ResolvedMembership[];
  active: ActiveContext | null;
  /**
   * True for the full duration of a context switch — the `switchContext`
   * round-trip AND the cache eviction + refetch it triggers — so the UI can
   * show one continuous indicator (top progress bar + switcher spinner).
   */
  isSwitching: boolean;
  setSwitching: (isSwitching: boolean) => void;
  /** The contexts the user may act in (for the dropdown + picker). */
  setMemberships: (memberships: ResolvedMembership[]) => void;
  /**
   * The now-active context — set ONLY when `switchContext` resolves (the server
   * session is the single source of truth; ENT-88/T17). Never seeded locally.
   */
  setActive: (active: ActiveContext) => void;
  /** Drop the active context (e.g. ACL_CONTEXT_REQUIRED) without wiping memberships. */
  clearActive: () => void;
  clear: () => void;
}

export const useContextStore = create<ContextState>((set) => ({
  memberships: [],
  active: null,
  isSwitching: false,
  setSwitching: (isSwitching) => set({ isSwitching }),
  // NOTE: no local seeding of `active`. The gateway reads the active context
  // from the server-side session written by `switchContext`, so the FE must
  // never assume a context is active until that mutation resolves — otherwise
  // the displayed org and the data diverge (the bug T17 fixes).
  setMemberships: (memberships) => set({ memberships }),
  setActive: (active) => set({ active }),
  clearActive: () => set({ active: null }),
  clear: () => set({ memberships: [], active: null }),
}));
